/**
 * usePrintDialog — Track 3 Phase 4
 *
 * Owns the PRINT / PLOT preview dialog feature (B11) extracted from
 * `App.tsx` as part of the Track 3 App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Owns the `printDialogOpen` flag and `printDialogSettings`
 *     (paper size / orientation / margins).
 *   - Exposes imperative `open()` / `close()` for the command-line
 *     router (`PRINT` / `PLOT` / `ПЕЧАТЬ`) and `resetShellState()`.
 *   - Computes `printPreviewWorldBounds` (via `useMemo`) — the paper
 *     rectangle in world space the canvas overlay should paint while
 *     the dialog is open. Returns `null` when the dialog is closed.
 *   - Owns the Confirm / Cancel handlers — both close the dialog and
 *     emit a localized command-feed entry.
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     props: { settings, onChange, onConfirm, onCancel },
 *     printPreviewWorldBounds,
 *     open,
 *     close,
 *   }
 *
 * The orchestrator mounts `<PrintPreviewDialog>` when `isOpen` and
 * threads `printPreviewWorldBounds` into `<Scene3DStage>`'s
 * `printPreviewWorldBounds` prop so the canvas overlay reflects the
 * current paper-size/orientation choice.
 *
 * AutoCAD parity:
 *   AutoCAD's PRINT / PLOT command opens the Plot dialog with paper
 *   size, plotter, scale, orientation, and the on-screen plot preview
 *   rectangle. The dialog component preserves the page-setup surface;
 *   the hook only owns orchestration and the world-space bounds derivation.
 */

import { useCallback, useMemo, useState } from "react";

import {
  defaultPlotSettings,
  PlotAreaType,
  PlotPaperUnits,
  PlotRotation,
  type LayoutPlotSettings,
} from "@dxf-ai-editor/drawing-model";

import {
  DEFAULT_PRINT_SETTINGS,
  resolvePaperDimensions,
  type PaperSizeId,
  type PrintArea,
  type PrintDialogSettings,
} from "../components/PrintPreviewDialog";
import {
  getEntityMetadataIndex,
  useEditorStore,
} from "../state/editorStore";
import {
  exportLayoutPdf,
  resolvePlotSettings,
} from "../print/pdf/exportLayoutPdf";
import { useSpaceContextStore } from "../state/spaceContextStore";
import { downloadPdfBytes } from "../print/pdf/downloadPdfBytes";
import {
  defaultPdfExportConfig,
  dpiToToleranceMm,
  type PdfExportConfig,
} from "../print/pdf/pdfExportConfig";
import { useT } from "../i18n";
import { useDialogOpenState } from "./useDialogOpenState";

/** A window rectangle in plotted-space world coordinates (codes 48/49/140/141). */
export interface PlotWindowRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Map the dialog's plot-area choice → the exporter's {@link PlotAreaType}. */
export function dialogAreaToPlotAreaType(area: PrintArea): PlotAreaType {
  switch (area) {
    case "extents":
      return PlotAreaType.Extents;
    case "window":
      return PlotAreaType.Window;
    case "layout":
    default:
      return PlotAreaType.Layout;
  }
}

/**
 * Bridge the lightweight PRINT-dialog settings (paper size / orientation /
 * margins / plot area, see PrintPreviewDialog) into the full
 * {@link LayoutPlotSettings} the vector PDF exporter consumes. The full DXF
 * PLOTSETTINGS parse is PDF-6; here we derive physical paper dimensions +
 * margins + the selected plot area from the dialog so the PLOT command
 * produces a real PDF download (PDF-1..PDF-4).
 *
 * PDF-4 made the exporter honour `plotAreaType` + `window`; this is the UI
 * integration (#177 P2). The dialog's Extents/Layout/Window selector now maps
 * straight through. For Window, the on-canvas preview rectangle (the same world
 * rect the overlay paints) supplies codes 48/49/140/141; absent a rect the
 * Window selection has no rectangle to fit, so it falls back to Extents (which
 * fits the drawing's own bounds — the closest non-fabricated behaviour, noted
 * in the exporter's regionNote).
 */
export function dialogToPlotSettings(
  dialog: PrintDialogSettings,
  windowRect?: PlotWindowRect | null,
): LayoutPlotSettings {
  const paperMm = resolvePaperDimensions(dialog.paperSize, dialog.orientation);
  const base = defaultPlotSettings();
  let plotAreaType = dialogAreaToPlotAreaType(dialog.area);
  const window = { ...base.window };
  if (plotAreaType === PlotAreaType.Window) {
    if (windowRect) {
      window.lowerLeftX = windowRect.minX;
      window.lowerLeftY = windowRect.minY;
      window.upperRightX = windowRect.maxX;
      window.upperRightY = windowRect.maxY;
    } else {
      // No rectangle captured → Window has nothing to fit; fall back to
      // Extents rather than fabricating a (0,0,0,0) window (which the exporter
      // would treat as a degenerate/empty region → blank page).
      plotAreaType = PlotAreaType.Extents;
    }
  }
  return {
    ...base,
    paperName: dialog.paperSize,
    paperWidthMm: paperMm.width,
    paperHeightMm: paperMm.height,
    units: PlotPaperUnits.Millimeters,
    margins: {
      left: dialog.margins.left,
      bottom: dialog.margins.bottom,
      right: dialog.margins.right,
      top: dialog.margins.top,
    },
    plotAreaType,
    window,
    rotation: PlotRotation.None,
  };
}

/** Map the exporter's {@link PlotAreaType} → the dialog's plot-area choice. */
export function plotAreaTypeToDialogArea(type: PlotAreaType): PrintArea {
  switch (type) {
    case PlotAreaType.Window:
      return "window";
    case PlotAreaType.Layout:
      return "layout";
    // Extents/Display/Limits/View all map to the dialog's "extents" — the only
    // non-Layout, non-Window choice the dialog exposes. The full DXF area type
    // is preserved on the model's `plotSettings`; this is only the lossy
    // projection onto the 3-option dialog selector.
    case PlotAreaType.Extents:
    case PlotAreaType.Display:
    case PlotAreaType.Limits:
    case PlotAreaType.View:
    default:
      return "extents";
  }
}

/** The dialog's paper-size catalogue (the {@link PaperSizeId} union values). */
const DIALOG_PAPER_IDS: readonly PaperSizeId[] = [
  "A0",
  "A1",
  "A2",
  "A3",
  "A4",
  "Letter",
  "Tabloid",
];

/**
 * Resolve a {@link LayoutPlotSettings.paperName} back to a dialog
 * {@link PaperSizeId}. `dialogToPlotSettings` stores the bare id ("A4"), so the
 * common round-trip is an exact match; an imported layout may instead carry a
 * descriptive name ("ISO A4 (210.00 x 297.00 MM)") so we also accept the id as
 * a case-insensitive substring (matching the longest id first so "A0".."A4"
 * don't shadow a longer token). Falls back to "A4" when nothing matches — the
 * dialog can only render its known catalogue, and the authoritative size lives
 * on the model's `plotSettings` regardless.
 */
export function resolveDialogPaperSize(paperName: string): PaperSizeId {
  const exact = DIALOG_PAPER_IDS.find((id) => id === paperName);
  if (exact) return exact;
  const upper = paperName.toUpperCase();
  // Longest id first so "Tabloid"/"Letter" win over no-op and "A4" isn't
  // shadowed when scanning (ids are mutually non-overlapping, but order keeps
  // the intent explicit).
  const byLength = [...DIALOG_PAPER_IDS].sort((a, b) => b.length - a.length);
  const sub = byLength.find((id) => upper.includes(id.toUpperCase()));
  return sub ?? "A4";
}

/**
 * Inverse of {@link dialogToPlotSettings} (PDF-6.6): project a layout's full
 * {@link LayoutPlotSettings} back onto the lightweight {@link PrintDialogSettings}
 * the PRINT dialog edits, so opening the dialog for a layout shows ITS persisted
 * page (paper size, orientation, margins, plot-area) instead of the hard-coded
 * {@link DEFAULT_PRINT_SETTINGS}.
 *
 * Orientation is derived from the physical paper dimensions (width > height →
 * landscape); the dialog has no separate orientation field on the model — it is
 * encoded by which of `paperWidthMm`/`paperHeightMm` is larger, exactly as
 * `dialogToPlotSettings` writes it (it swaps the catalogue dims for landscape).
 */
export function plotSettingsToDialog(
  settings: LayoutPlotSettings,
): PrintDialogSettings {
  const orientation: PrintDialogSettings["orientation"] =
    settings.paperWidthMm > settings.paperHeightMm ? "landscape" : "portrait";
  return {
    paperSize: resolveDialogPaperSize(settings.paperName),
    orientation,
    margins: {
      left: settings.margins.left,
      right: settings.margins.right,
      top: settings.margins.top,
      bottom: settings.margins.bottom,
    },
    area: plotAreaTypeToDialogArea(settings.plotAreaType),
  };
}

/**
 * Bounding box of the PLOTTED (active) space's entities (FIX F, Codex #180).
 *
 * The metadata index exposes no per-space aggregate bound (only the GLOBAL
 * `aggregates().totalBounds` across every space), so we fold the active space's
 * per-entity bounds via `filterBySpace(spaceId)` — each returned record carries
 * its own axis-aligned `bounds`. Returns `null` when the space has no entities
 * (the preview then falls back to a paper-sized rect at the origin, matching the
 * empty-document behaviour). Non-finite per-entity bounds are skipped.
 */
export function activeSpaceBounds(
  spaceId: string,
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  const records = getEntityMetadataIndex().filterBySpace(spaceId);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let any = false;
  for (const rec of records) {
    const b = rec.bounds;
    if (
      !Number.isFinite(b.minX) ||
      !Number.isFinite(b.minY) ||
      !Number.isFinite(b.maxX) ||
      !Number.isFinite(b.maxY)
    ) {
      continue;
    }
    if (b.minX < minX) minX = b.minX;
    if (b.minY < minY) minY = b.minY;
    if (b.maxX > maxX) maxX = b.maxX;
    if (b.maxY > maxY) maxY = b.maxY;
    any = true;
  }
  return any ? { minX, minY, maxX, maxY } : null;
}

export interface UsePrintDialogParams {
  /** Receives localized open / confirm / cancel feed entries. */
  appendCommandFeed: (text: string) => void;
  /**
   * PDF-6.9b — non-reactive read of the committed PDF Options config. The
   * print path threads the vector DPI into `toleranceMm` (via
   * `dpiToToleranceMm`) and the omitted-layer list straight through to the
   * exporter. Optional — when absent the hook uses `defaultPdfExportConfig()`
   * so the pre-6.9b behaviour is preserved (1200 dpi, no omitted layers).
   * The host wires this to `usePdfOptionsDialog.getConfig`.
   */
  getPdfOptionsConfig?: () => PdfExportConfig;
}

export interface UsePrintDialogResult {
  /**
   * Whether to mount `<PrintPreviewDialog>`. False while a Window-pick is in
   * progress (the dialog is hidden so the canvas is reachable) even though
   * the PRINT command is still "active" — track that via {@link windowPick}.
   */
  isOpen: boolean;
  /** Props bundle for `<PrintPreviewDialog>`. */
  props: {
    settings: PrintDialogSettings;
    onChange: (next: PrintDialogSettings) => void;
    onConfirm: () => void;
    onCancel: () => void;
    /** PDF-6.7 — start the interactive plot Window-pick. */
    onPickWindow: () => void;
  };
  /**
   * Paper rectangle in world space — fed to `<Scene3DStage>` so the
   * canvas overlay paints a translucent preview rectangle while the
   * dialog is open. `null` while the dialog is closed.
   *
   * PDF-6.7: for plot-area = Window WITH a picked/persisted window rect,
   * this is the WINDOW rectangle itself (so the overlay shows exactly what
   * will plot) rather than the aspect-fitted auto preview.
   */
  printPreviewWorldBounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    label: string;
  } | null;
  /**
   * PDF-6.7 — interactive Window-pick state + callbacks, threaded into
   * `<Scene3DStage>`'s `windowPickActive` / `onWindowPicked` /
   * `onWindowPickCancel`.
   */
  windowPick: {
    /** True while the user is picking the plot window on the canvas. */
    isPicking: boolean;
    /** Two corners picked → store + persist the window, re-show the dialog. */
    onPicked: (rect: PlotWindowRect) => void;
    /** Pick aborted (Esc / right-click) → re-show the dialog, window unchanged. */
    onCancel: () => void;
  };
  /** Imperative open — used by the command-line router (PRINT/PLOT). */
  open: () => void;
  /** Imperative close — used by `resetShellState()`. */
  close: () => void;
}

export function usePrintDialog({
  appendCommandFeed,
  getPdfOptionsConfig,
}: UsePrintDialogParams): UsePrintDialogResult {
  const t = useT();
  const entityMetadataIndexVersion = useEditorStore(
    (store) => store.entityMetadataIndexVersion,
  );
  // The plotted (active) space drives the Window preview bounds (FIX F, Codex
  // #180): the preview rect — and the exported Window rectangle reusing it —
  // must come from the plotted space, NOT the GLOBAL totalBounds across every
  // space. Reactive so switching the active layout recomputes the preview.
  const activeSpaceId = useEditorStore(
    (store) => store.geometryDocument.activeSpaceId,
  );

  const [settings, setSettings] = useState<PrintDialogSettings>(
    DEFAULT_PRINT_SETTINGS,
  );
  // PDF-6.7 — the plot window rect (world / paper-mm) for the current dialog
  // session. Seeded on open from the layout's persisted PLOTSETTINGS window
  // (when its plotAreaType is Window), updated when the user picks a new
  // window on the canvas. `null` = no window captured yet (Window selection
  // then falls back to Extents, matching `dialogToPlotSettings`).
  const [pickedWindow, setPickedWindow] = useState<PlotWindowRect | null>(null);
  // PDF-6.7 — true while the user is rubber-banding the window on the canvas
  // (the dialog is hidden meanwhile so the canvas receives the pointer).
  const [isPicking, setIsPicking] = useState(false);
  // PDF-6.7 P2 #2 — the layout active at the moment the dialog was OPENED.
  // Persist-on-confirm writes back to THIS layout, not whichever tab happens
  // to be active when the user later clicks Confirm (guards mid-flow tab switches).
  const [dialogOriginSpaceId, setDialogOriginSpaceId] = useState<string | null>(
    null,
  );
  const { isOpen, open: openDialog, close: closeDialog } = useDialogOpenState();

  // Wrap close so tearing down the dialog also clears any in-progress pick —
  // `resetShellState()` (and the cancel path) must not leave the canvas stuck
  // in window-pick mode with the dialog gone.
  const close = useCallback(() => {
    setIsPicking(false);
    setDialogOriginSpaceId(null);
    closeDialog();
  }, [closeDialog]);

  const open = useCallback(() => {
    // PDF-6.6 — LOAD ON OPEN: seed the dialog from the ACTIVE layout's persisted
    // PLOTSETTINGS in the model (source of truth) so each layout remembers its
    // own plot area / paper / scale / window. Read non-reactively at open time
    // so the seed reflects the layout active right now (no stale closure).
    //
    // MODEL tab (and any space with no LAYOUT object): keep the historical
    // DEFAULT_PRINT_SETTINGS (A4 portrait, Extents) — AutoCAD has no LAYOUT
    // object for model space, and `resolvePlotSettings` would otherwise return
    // the Layout-area page default which is wrong for the model tab.
    const doc = useEditorStore.getState().geometryDocument;
    const space = doc.spaces.find((s) => s.id === doc.activeSpaceId);
    if (space?.kind === "layout") {
      // For a layout NEVER edited, `resolvePlotSettings` returns its seeded
      // `defaultPlotSettings()` (PDF-6.1); once persisted (write-on-confirm) it
      // returns the saved page. Either way the dialog opens on the real model.
      const resolved = resolvePlotSettings(doc, doc.activeSpaceId);
      setSettings(plotSettingsToDialog(resolved));
      // PDF-6.7 — seed the session window rect from the persisted PLOTSETTINGS
      // when the layout's saved area IS Window AND the saved rectangle is
      // non-degenerate; otherwise start with no window (Window selection then
      // prompts a fresh pick, falling back to Extents until one is set).
      if (resolved.plotAreaType === PlotAreaType.Window) {
        const w = resolved.window;
        const minX = Math.min(w.lowerLeftX, w.upperRightX);
        const minY = Math.min(w.lowerLeftY, w.upperRightY);
        const maxX = Math.max(w.lowerLeftX, w.upperRightX);
        const maxY = Math.max(w.lowerLeftY, w.upperRightY);
        setPickedWindow(
          maxX - minX > 1e-9 && maxY - minY > 1e-9
            ? { minX, minY, maxX, maxY }
            : null,
        );
      } else {
        setPickedWindow(null);
      }
    } else {
      setSettings(DEFAULT_PRINT_SETTINGS);
      setPickedWindow(null);
    }
    // Capture the layout active at OPEN time so persist-on-confirm targets
    // it (mid-flow tab switches must not redirect the write).
    setDialogOriginSpaceId(doc.activeSpaceId);
    openDialog();
    appendCommandFeed(t("print.feed.opened"));
  }, [appendCommandFeed, openDialog, t]);

  // While the dialog is open, paint a translucent rectangle on the
  // canvas tracing the printable area (orientation + margins applied).
  // The paper rectangle is positioned around the drawing's centroid
  // and aspect-fitted; if the document is empty, we anchor at (0,0)
  // so the orientation toggle still gives visual feedback.
  //
  // Declared BEFORE onConfirm so the Window plot-area export can reuse this
  // exact rect as the plot window (codes 48/49/140/141) — the overlay and the
  // exported window stay in lockstep (#177 P2 — PDF-4 UI integration).
  const printPreviewWorldBounds = useMemo<{
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    label: string;
  } | null>(() => {
    if (!isOpen) return null;
    // PDF-6.7 — when plot-area = Window AND a window rect has been picked /
    // persisted, the preview rect IS the window rect (so the on-canvas overlay
    // shows exactly what will plot), not the aspect-fitted auto preview. The
    // label marks it as the plot window. This is also the rect `onConfirm`
    // persists (codes 48/49/140/141), keeping overlay ↔ export in lockstep.
    if (settings.area === "window" && pickedWindow) {
      return {
        minX: pickedWindow.minX,
        minY: pickedWindow.minY,
        maxX: pickedWindow.maxX,
        maxY: pickedWindow.maxY,
        label: `${settings.paperSize} ▭`,
      };
    }
    // FIX F (Codex #180): bound the preview to the PLOTTED (active) space, not
    // the global totalBounds. The metadata index has no per-space aggregate
    // bound, so we fold the active space's per-entity bounds here
    // (`filterBySpace` returns records carrying each entity's bounds). This
    // keeps the on-canvas preview rect AND the exported Window rectangle (which
    // reuses this exact rect) in lockstep, sourced from the plotted space only.
    const total = activeSpaceBounds(activeSpaceId);
    // Paper printable area in mm (orientation + margins applied).
    const paperMm = resolvePaperDimensions(
      settings.paperSize,
      settings.orientation,
    );
    const printableMm = {
      width: Math.max(
        paperMm.width - settings.margins.left - settings.margins.right,
        1,
      ),
      height: Math.max(
        paperMm.height - settings.margins.top - settings.margins.bottom,
        1,
      ),
    };
    const aspect = printableMm.width / printableMm.height;
    // No content yet — anchor a paper-sized rectangle around (0, 0)
    // so the user still sees the orientation / size impact while
    // tweaking the dialog.
    let center = { x: 0, y: 0 };
    let worldWidth = 100;
    let worldHeight = 100 / aspect;
    if (total) {
      center = {
        x: (total.minX + total.maxX) / 2,
        y: (total.minY + total.maxY) / 2,
      };
      const totalWidth = Math.max(total.maxX - total.minX, 1);
      const totalHeight = Math.max(total.maxY - total.minY, 1);
      const totalAspect = totalWidth / totalHeight;
      // Aspect-fit: the paper rectangle in world space wraps the
      // entire drawing. Whichever side is the limiter dictates scale.
      if (totalAspect > aspect) {
        worldWidth = totalWidth * 1.05;
        worldHeight = worldWidth / aspect;
      } else {
        worldHeight = totalHeight * 1.05;
        worldWidth = worldHeight * aspect;
      }
    }
    return {
      minX: center.x - worldWidth / 2,
      minY: center.y - worldHeight / 2,
      maxX: center.x + worldWidth / 2,
      maxY: center.y + worldHeight / 2,
      label: `${settings.paperSize} ${settings.orientation === "portrait" ? "↕" : "↔"}`,
    };
  }, [
    isOpen,
    settings.area,
    settings.paperSize,
    settings.orientation,
    settings.margins.left,
    settings.margins.right,
    settings.margins.top,
    settings.margins.bottom,
    pickedWindow,
    entityMetadataIndexVersion,
    activeSpaceId,
  ]);

  const onConfirm = useCallback(() => {
    appendCommandFeed(
      t("print.feed.completed", {
        size: settings.paperSize,
        orientation: t(
          settings.orientation === "portrait"
            ? "print.dialog.orientation.portrait"
            : "print.dialog.orientation.landscape",
        ),
      }),
    );
    close();

    // Vector PDF export (PDF-1..PDF-4). The active layout's paper-space
    // geometry is rendered to a vector PDF and downloaded. Async +
    // fire-and-forget so the dialog closes immediately; feed entries report the
    // outcome. The dialog's plot-area selection (Extents/Layout/Window) is
    // honoured by the exporter (PDF-4); for Window the on-canvas preview rect
    // supplies the window rectangle (codes 48/49/140/141).
    const store = useEditorStore.getState();
    const doc = store.geometryDocument;
    // PDF-6.7: for Window, persist + export the INTERACTIVELY PICKED window
    // rect (codes 48/49/140/141). Falls back to the auto preview rect (the
    // pre-6.7 behaviour) only when no explicit window was picked, so the
    // Window selection always has a concrete rectangle. `dialogToPlotSettings`
    // falls back to Extents when both are absent (no fabricated 0-window).
    const windowRect =
      settings.area === "window"
        ? (pickedWindow ??
          (printPreviewWorldBounds
            ? {
                minX: printPreviewWorldBounds.minX,
                minY: printPreviewWorldBounds.minY,
                maxX: printPreviewWorldBounds.maxX,
                maxY: printPreviewWorldBounds.maxY,
              }
            : null))
        : null;
    const plotSettings = dialogToPlotSettings(settings, windowRect);

    // PDF-6.6 — WRITE ON CONFIRM (persist): commit the dialog's choice to the
    // ACTIVE layout's `plotSettings` in the model (source of truth) so the
    // layout remembers its plot area / window / scale across tab switches and
    // future plots. The store action no-ops for the MODEL tab (no LAYOUT
    // object); for a paper-space layout it updates both `layoutsBySpaceId` and
    // the by-name `layouts` dict. The export below still passes `settings`
    // explicitly (the PDF-4 transient-to-export path other callers rely on),
    // which now matches the just-persisted page.
    // Persist to the layout the dialog was OPENED for, not the currently-active
    // one — guards against mid-flow tab switches (PDF-6.7 P2 #2).
    const targetSpaceId = dialogOriginSpaceId ?? doc.activeSpaceId;
    store.setLayoutPlotSettings(targetSpaceId, plotSettings);

    const fileName = `${doc.title || "drawing"}-${settings.paperSize}.pdf`;
    // PDF-6.9b: read the committed PDF Options config NON-REACTIVELY so the
    // export reflects the user's last OK click (`onConfirm` on the sub-modal).
    // Falls back to defaults when the dialog was never opened.
    const pdfOptions = getPdfOptionsConfig?.() ?? defaultPdfExportConfig();
    // Target the dialog's originating layout for export too (mirrors the
    // persist target above) so a mid-flow tab switch doesn't redirect the
    // exporter to the newly-active layout (Codex P2 #1, round 2).
    void exportLayoutPdf(doc, {
      spaceId: targetSpaceId,
      settings: plotSettings,
      title: doc.title,
      // PDF-6.9b — vector DPI → tessellation tolerance (half-pixel at the
      // chosen print resolution). Higher DPI = finer chords on arcs / curves.
      toleranceMm: dpiToToleranceMm(pdfOptions.vectorDpi),
      // PDF-6.9b — layer NAMES the user un-checked. The exporter skips
      // geometry, OCG entries, and VP frames for these layers.
      omitLayers: pdfOptions.omitLayers,
      // PDF-6.3b — loaded plot-style table (CTB/STB). Threaded into the
      // sheet renderer's CadConvertContext so per-entity style resolution
      // applies the override paths (colour, lineweight, linetype, alpha,
      // do-not-plot).
      ...(pdfOptions.plotStyleTable
        ? { plotStyleTable: pdfOptions.plotStyleTable.table }
        : {}),
      // PDF-6.4 — "Lines merge / Overwrite" session-level setting. "merge"
      // wraps every painted run in `/BM /Multiply` so overlapping geometry
      // darkens (AutoCAD PC3 parity). Default "overwrite" preserves the
      // pre-6.4 byte-identical output.
      mergeControl: pdfOptions.mergeControl,
    })
      .then((result) => {
        downloadPdfBytes(result.bytes, fileName);
        appendCommandFeed(
          t("print.feed.exported", {
            paths: String(result.pathCount),
            size: settings.paperSize,
          }),
        );
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        appendCommandFeed(t("print.feed.export_failed", { message }));
      });
  }, [
    appendCommandFeed,
    close,
    settings,
    pickedWindow,
    printPreviewWorldBounds,
    dialogOriginSpaceId,
    getPdfOptionsConfig,
    t,
  ]);

  const onCancel = useCallback(() => {
    appendCommandFeed(t("print.feed.cancelled"));
    close();
  }, [appendCommandFeed, close, t]);

  // PDF-6.7 — start the interactive plot Window-pick. AutoCAD parity: the
  // Plot dialog hides, the user picks two corners on the canvas, then the
  // dialog returns with the window set. We force the dialog's plot-area to
  // Window (the button is only shown for Window, but this also covers a
  // programmatic call) and hide the dialog by toggling `isPicking` — the
  // host gates `isOpen` on `!isPicking` so `<PrintPreviewDialog>` unmounts
  // and the canvas receives the pick pointer.
  const onPickWindow = useCallback(() => {
    // PLOT-Window pick is a paper-space op; auto-exit MSPACE_VIA_VP so the
    // picker uses the paper camera (matches AutoCAD: PLOT exits to PSPACE first).
    const spaceCtx = useSpaceContextStore.getState();
    if (spaceCtx.mode === "MSPACE_VIA_VP") {
      spaceCtx.exitViewport();
      // Mirror the viewport-exit shellUi sync used by Scene3DStage exit paths
      // (Scene3DStage.tsx:1354) so editor-state `drawing.layoutContext` stays
      // in sync with the spaceContextStore (Codex P2 #3, round 2).
      useEditorStore.getState().dispatch({
        type: "shell/set-layout-context",
        source: "scene",
        layoutContext: "paper",
      });
    }
    setSettings((prev) =>
      prev.area === "window" ? prev : { ...prev, area: "window" },
    );
    setIsPicking(true);
    appendCommandFeed(t("print.feed.window_pick_start"));
  }, [appendCommandFeed, t]);

  // PDF-6.7 — two corners picked on the canvas. Store the rect for the
  // session and re-show the dialog (which now previews the picked window).
  // Persist is DEFERRED to `onConfirm` so Cancel doesn't leave a stale window
  // on the layout; the originating layout is captured at `open()` so a
  // mid-flow tab switch doesn't redirect the eventual write.
  const onWindowPicked = useCallback(
    (rect: PlotWindowRect) => {
      setPickedWindow(rect);
      setIsPicking(false);
      appendCommandFeed(t("print.feed.window_pick_set"));
    },
    [appendCommandFeed, t],
  );

  // PDF-6.7 — pick aborted (Esc / right-click). Re-show the dialog with the
  // window unchanged (whatever was picked/persisted before, if anything).
  const onWindowPickCancel = useCallback(() => {
    setIsPicking(false);
    appendCommandFeed(t("print.feed.window_pick_cancelled"));
  }, [appendCommandFeed, t]);

  return {
    // PDF-6.7 — hide the dialog while a Window-pick is in progress so the
    // canvas is reachable; the PRINT command is still "active" (tracked via
    // `windowPick.isPicking`). The host re-shows it when the pick ends.
    isOpen: isOpen && !isPicking,
    props: {
      settings,
      onChange: setSettings,
      onConfirm,
      onCancel,
      onPickWindow,
    },
    printPreviewWorldBounds,
    windowPick: {
      isPicking: isOpen && isPicking,
      onPicked: onWindowPicked,
      onCancel: onWindowPickCancel,
    },
    open,
    close,
  };
}
